import { Injectable, ElementRef, OnDestroy } from '@angular/core';

export interface MutationObserverConfig {
    childList?: boolean;
    subtree?: boolean;
    attributes?: boolean;
    attributeFilter?: string[];
    characterData?: boolean;
    attributeOldValue?: boolean;
    characterDataOldValue?: boolean;
}

export interface MutationCallback {
    (mutations: MutationRecord[], observer: MutationObserver): void;
}

export interface ElementWatchConfig {
    element: ElementRef | Element;
    callback: MutationCallback;
    config?: MutationObserverConfig;
    selector?: string; // Optional CSS selector to watch for specific elements
    onElementFound?: (element: Element) => void; // Callback when target element is found
    stopOnFirstMatch?: boolean; // Stop observing after first match
}

@Injectable({
    providedIn: 'root'
})
export class DomMutationObserverService implements OnDestroy {
    private observers = new Map<string, MutationObserver>();
    private elementWatchers = new Map<string, ElementWatchConfig>();

    ngOnDestroy(): void {
        this.disconnectAll();
    }

    /**
     * Create a mutation observer for a specific element
     */
    observeElement(
        id: string,
        element: ElementRef | Element,
        callback: MutationCallback,
        config: MutationObserverConfig = { childList: true, subtree: true }
    ): MutationObserver {
        // Disconnect existing observer with same ID
        this.disconnect(id);

        const targetElement = element instanceof ElementRef ? element.nativeElement : element;
        const observer = new MutationObserver(callback);

        observer.observe(targetElement, config);
        this.observers.set(id, observer);

        return observer;
    }

    /**
     * Watch for specific elements to appear in the DOM
     * This is perfect for replacing setTimeout calls that wait for elements
     */
    watchForElement(
        id: string,
        element: ElementRef | Element,
        selector: string,
        onElementFound: (element: Element) => void,
        config: MutationObserverConfig = { childList: true, subtree: true },
        stopOnFirstMatch: boolean = true
    ): MutationObserver {
        const targetElement = element instanceof ElementRef ? element.nativeElement : element;

        const callback: MutationCallback = (mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    // Check added nodes
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as Element;

                            // Check if the added node matches our selector
                            if (element.matches && element.matches(selector)) {
                                onElementFound(element);
                                if (stopOnFirstMatch) {
                                    this.disconnect(id);
                                    return;
                                }
                            }

                            // Check if any child of the added node matches our selector
                            const foundElement = element.querySelector(selector);
                            if (foundElement) {
                                onElementFound(foundElement);
                                if (stopOnFirstMatch) {
                                    this.disconnect(id);
                                    return;
                                }
                            }
                        }
                    });
                }
            });
        };

        return this.observeElement(id, targetElement, callback, config);
    }

    /**
     * Watch for multiple elements with different selectors
     */
    watchForMultipleElements(
        id: string,
        element: ElementRef | Element,
        selectors: { [key: string]: (element: Element) => void },
        config: MutationObserverConfig = { childList: true, subtree: true }
    ): MutationObserver {
        const targetElement = element instanceof ElementRef ? element.nativeElement : element;

        const callback: MutationCallback = (mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as Element;

                            // Check each selector
                            Object.keys(selectors).forEach((selector) => {
                                const callback = selectors[selector];
                                if (callback) {
                                    if (element.matches && element.matches(selector)) {
                                        callback(element);
                                    }

                                    const foundElement = element.querySelector(selector);
                                    if (foundElement) {
                                        callback(foundElement);
                                    }
                                }
                            });
                        }
                    });
                }
            });
        };

        return this.observeElement(id, targetElement, callback, config);
    }

    /**
     * Watch for elements with a timeout fallback
     * This combines the reliability of MutationObserver with setTimeout as backup
     */
    watchForElementWithTimeout(
        id: string,
        element: ElementRef | Element,
        selector: string,
        onElementFound: (element: Element) => void,
        timeoutMs: number = 5000,
        config: MutationObserverConfig = { childList: true, subtree: true }
    ): { observer: MutationObserver; timeoutId: number } {
        const targetElement = element instanceof ElementRef ? element.nativeElement : element;
        let elementFound = false;

        // Set up timeout as fallback
        const timeoutId = window.setTimeout(() => {
            if (!elementFound) {
                // Element with selector not found within timeout
                this.disconnect(id);
            }
        }, timeoutMs);

        // Set up mutation observer
        const observer = this.watchForElement(
            id,
            targetElement,
            selector,
            (foundElement) => {
                if (!elementFound) {
                    elementFound = true;
                    clearTimeout(timeoutId);
                    onElementFound(foundElement);
                }
            },
            config,
            true
        );

        return { observer, timeoutId };
    }

    /**
     * Watch for click actions to be added (perfect for LinkHandler scenarios)
     */
    watchForClickActions(
        id: string,
        element: ElementRef | Element,
        onClickActionsFound: (anchorElements: HTMLAnchorElement[]) => void,
        config: MutationObserverConfig = { childList: true, subtree: true }
    ): MutationObserver {
        const targetElement = element instanceof ElementRef ? element.nativeElement : element;

        const callback: MutationCallback = (mutations) => {
            let hasNewAnchors = false;

            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as Element;

                            // Check if this element or its children have click action attributes
                            const anchorsWithClickActions = element.querySelectorAll('a[data-click_action_choice]');
                            if (anchorsWithClickActions.length > 0) {
                                hasNewAnchors = true;
                            }

                            // Also check if the element itself is an anchor with click actions
                            if (element.tagName === 'A' && element.hasAttribute('data-click_action_choice')) {
                                hasNewAnchors = true;
                            }
                        }
                    });
                }
            });

            if (hasNewAnchors) {
                const allAnchorsWithClickActions = targetElement.querySelectorAll('a[data-click_action_choice]');
                onClickActionsFound(Array.from(allAnchorsWithClickActions));
            }
        };

        return this.observeElement(id, targetElement, callback, config);
    }

    /**
     * Disconnect a specific observer
     */
    disconnect(id: string): void {
        const observer = this.observers.get(id);
        if (observer) {
            observer.disconnect();
            this.observers.delete(id);
        }
        this.elementWatchers.delete(id);
    }

    /**
     * Disconnect all observers
     */
    disconnectAll(): void {
        this.observers.forEach((observer) => {
            observer.disconnect();
        });
        this.observers.clear();
        this.elementWatchers.clear();
    }

    /**
     * Check if an observer exists
     */
    hasObserver(id: string): boolean {
        return this.observers.has(id);
    }

    /**
     * Get all active observer IDs
     */
    getActiveObserverIds(): string[] {
        return Array.from(this.observers.keys());
    }
}
